import classNames from 'classnames';
import { observer } from 'mobx-react';
import React from 'react';
import { BackendSourceOptions, discoveryTracker } from '@udemy/browse-event-tracking';
import { ClickEvent, Tracker } from '@udemy/event-tracking';
import { useMatchMedia } from '@udemy/hooks';
import { useI18n } from '@udemy/i18n';
import { SuspenseUntilInView, TextSkeleton } from '@udemy/react-reveal-components/';
import { CourseUnitSkeletonVerticalLayout } from '../course-unit/course-unit-skeleton.react-component';
import { SkillsHubUnit } from '../skills-hub-unit/skills-hub-unit.react-component';
import { professionalSkillsShowcaseData } from './data/professional-skills-showcase';
import { ProfessionalSkillsUnitStore } from './professional-skills-unit.mobx-store';
import styles from "./professional-skills-unit.module.css";
const SubcategoryContentPanelLazy = /*#__PURE__*/React.lazy(() => import('./subcategory-content-panel.react-component').then(module => ({
  default: module.SubcategoryContentPanel
})));

/**
 * Props for the `ProfessionalSkillsUnit` component
 */

/**
 * Discovery unit that renders an expanded version of the `SkillsHubUnit` with a topic
 * pill carousel at the top and a different course carousel layout using `BorderedCourseCard`
 */
export const ProfessionalSkillsUnit = observer(({
  className,
  contentPanelClassName,
  courseCardProps,
  headingClassName = 'ud-heading-serif-xxl',
  heading,
  subheading,
  unitData = professionalSkillsShowcaseData,
  contentLoadingSkeleton,
  showSkillFilters = true,
  showStackedProductCardLayout = false,
  showAddToCartInStackedProductCardLayout = false,
  showAllButtonStyle,
  showBackgroundColor = true
}) => {
  const isMobileMax = useMatchMedia('mobile-max');
  const {
    gettext
  } = useI18n();
  const [store] = React.useState(new ProfessionalSkillsUnitStore(unitData));
  const stackedLayoutProps = {
    layout: 'singlecol',
    showStackedProductCardLayout: true,
    showTitle: false,
    disableInfiniteScroll: true,
    showAddToCartInCourseCard: showAddToCartInStackedProductCardLayout,
    addToCartButtonStyle: 'primary',
    addToCartButtonSize: 'xsmall',
    courseCardProps: {
      layout: 'vertical',
      showCourseTag: false,
      showDescription: false,
      showDetails: false,
      showRibbonsLast: true,
      disablePremiumHighlightedRibbon: true,
      showRibbons: true
    },
    showInstructorDetails: true,
    isMobileMax: isMobileMax !== null && isMobileMax !== void 0 ? isMobileMax : undefined,
    showQuickViewBox: false,
    maxCardsPerStack: 3,
    maxCards: 3,
    showDividers: false,
    loadingSkeleton: /*#__PURE__*/React.createElement(CourseUnitSkeletonVerticalLayout, null)
  };
  const shouldShowStackedLayout = showStackedProductCardLayout && isMobileMax;
  React.useEffect(() => {
    store.populateSubcategoryAndTopicData();
  }, [store]);
  const onCarouselPagerButtonClick = () => Tracker.publishEvent(new ClickEvent({
    componentName: 'carousel'
  }));

  /* istanbul ignore next */
  const getTabTrackingContext = () => {
    return {
      onTabImpression: (subUnit, index) => trackTabAction(discoveryTracker.trackDiscoveryImpression, subUnit, index, true),
      onTabSelect: (subUnit, index) => trackTabAction(discoveryTracker.trackDiscoveryItemClickEvent, subUnit, index),
      onCarouselPagerButtonClick
    };
  };

  /* istanbul ignore next */
  const trackTabAction = (trackFunc, unit, index, isImpressionEvent) => {
    var _unit$available_filte, _unit$available_filte2;
    const itemId = showSkillFilters ? unit.id : isImpressionEvent ? unit.label_id : (_unit$available_filte = unit.available_filters) === null || _unit$available_filte === void 0 ? void 0 : _unit$available_filte.units[0].label_id;
    const {
      item,
      trackingContext
    } = {
      item: {
        id: itemId,
        title: showSkillFilters ? unit.title : ((_unit$available_filte2 = unit.available_filters) === null || _unit$available_filte2 === void 0 ? void 0 : _unit$available_filte2.units[0].title) || '',
        type: showSkillFilters ? 'course_subcategory' : 'course_topic',
        frontendTrackingId: unit.frontendTrackingId
      },
      trackingContext: {
        backendSource: BackendSourceOptions.DISCOVERY,
        index: index,
        uiRegion: 'skills_to_transform'
      }
    };
    // Don't track if item id is undefined. This can happen when accessing
    // unit.available_filters?.units[0] and the units array is empty.
    //
    // Possible causes for empty units array:
    // 1. Topic doesn't require additional categorization
    // 2. Backend returns topic without nested sub-filters/refinements configured
    // 3. Filtering logic removed all items (e.g., user preferences, availability)
    if (itemId) {
      trackFunc({
        item
      }, trackingContext);
    }
  };

  /* istanbul ignore next */
  const trackShowAllCtaClick = unit => {
    var _unit$available_filte3;
    return Tracker.publishEvent(new ClickEvent({
      componentName: 'browseMoreCourses',
      relatedObjectType: showSkillFilters ? 'course_subcategory' : 'course_topic',
      relatedObjectId: showSkillFilters ? unit.id : (_unit$available_filte3 = unit.available_filters) === null || _unit$available_filte3 === void 0 ? void 0 : _unit$available_filte3.units[0].label_id
    }));
  };
  const renderContent = React.useCallback(unit => {
    return /*#__PURE__*/React.createElement(SuspenseUntilInView, {
      fallback: null
    }, /*#__PURE__*/React.createElement(SubcategoryContentPanelLazy, Object.assign({
      className: contentPanelClassName,
      unit: unit,
      onShowAllClick: () => trackShowAllCtaClick(unit),
      onCarouselPagerButtonClick: onCarouselPagerButtonClick,
      loadingSkeleton: contentLoadingSkeleton,
      showSkillFilters: showSkillFilters,
      showAllButtonStyle: showAllButtonStyle,
      courseCardProps: courseCardProps
    }, shouldShowStackedLayout && stackedLayoutProps)));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [contentPanelClassName, contentLoadingSkeleton, showSkillFilters, shouldShowStackedLayout, stackedLayoutProps]);
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    "data-testid": "professional-skills-unit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "professional-skills-unit-header"
  }, /*#__PURE__*/React.createElement("h2", {
    className: classNames(headingClassName)
  }, heading !== null && heading !== void 0 ? heading : gettext('All the skills you need in one place')), subheading !== '' && /*#__PURE__*/React.createElement("div", {
    className: classNames('ud-text-lg', styles.subtitle)
  }, subheading !== null && subheading !== void 0 ? subheading : gettext('From critical skills to technical topics, Udemy supports your professional development.'))), store.isLoading ? /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['skills-hub-unit'], {
      [styles['skill-filters']]: showSkillFilters
    })
  }, /*#__PURE__*/React.createElement(TextSkeleton, {
    withTitle: true,
    lineCountPerParagraph: 3,
    paragraphCount: 1,
    className: styles['loading-title']
  }), contentLoadingSkeleton) : /*#__PURE__*/React.createElement(SkillsHubUnit, Object.assign({
    className: classNames(styles['skills-hub-unit'], {
      [styles['skill-filters']]: showSkillFilters,
      [styles['tab-content-background']]: showBackgroundColor
    }),
    unit: store.unitData,
    renderContent: renderContent,
    tabTrackingContext: getTabTrackingContext(),
    loadingSkeleton: contentLoadingSkeleton,
    showSkillFilters: showSkillFilters,
    courseCardProps: courseCardProps
  }, shouldShowStackedLayout && stackedLayoutProps)));
});
//# sourceMappingURL=professional-skills-unit.react-component.js.map